import { collection, deleteDoc, doc, getDoc, getDocs, setDoc } from "firebase/firestore";
import { deleteObject, listAll, ref } from "firebase/storage";
import { db, storage } from "../firebase.js";
import {
  PROPERTIES_COLLECTION,
  createPropertyDocumentPath,
  createPropertyStoragePath,
  createUnitDocumentId,
  createUnitsCollectionPath,
  createUnitStoragePath,
  normalizeAvailabilityDate,
  normalizeProperty,
} from "./config.js";

async function deleteStorageDirectory(storagePath) {
  if (!storagePath) {
    return;
  }

  async function clearDirectory(storageRef) {
    const result = await listAll(storageRef);

    await Promise.all(
      result.items.map(async (itemRef) => {
        try {
          await deleteObject(itemRef);
        } catch (error) {
          if (error.code !== "storage/object-not-found") {
            throw error;
          }
        }
      })
    );

    await Promise.all(result.prefixes.map((prefixRef) => clearDirectory(prefixRef)));
  }

  await clearDirectory(ref(storage, storagePath));
}

async function loadUnits(propertyId, propertyStoragePath) {
  const unitsSnapshot = await getDocs(collection(db, PROPERTIES_COLLECTION, propertyId, "Units"));

  return unitsSnapshot.docs.map((unitDocument) => ({
    id: unitDocument.id,
    data: {
      ...unitDocument.data(),
      propertyStoragePath,
    },
  }));
}

export async function listProperties() {
  const propertiesSnapshot = await getDocs(collection(db, PROPERTIES_COLLECTION));
  const properties = await Promise.all(
    propertiesSnapshot.docs.map(async (propertyDocument) => {
      const propertyData = propertyDocument.data() || {};
      const propertyStoragePath =
        propertyData.propertyStoragePath ||
        propertyData.storagePath ||
        createPropertyStoragePath(propertyDocument.id);
      const units = await loadUnits(propertyDocument.id, propertyStoragePath);

      return normalizeProperty(propertyDocument.id, propertyData, units);
    })
  );

  return properties.sort((firstProperty, secondProperty) =>
    firstProperty.label.localeCompare(secondProperty.label)
  );
}

export async function getProperty(propertyId) {
  if (!propertyId) {
    return null;
  }

  const propertySnapshot = await getDoc(doc(db, createPropertyDocumentPath(propertyId)));

  if (!propertySnapshot.exists()) {
    return null;
  }

  const propertyData = propertySnapshot.data() || {};
  const propertyStoragePath =
    propertyData.propertyStoragePath ||
    propertyData.storagePath ||
    createPropertyStoragePath(propertySnapshot.id);
  const units = await loadUnits(propertySnapshot.id, propertyStoragePath);

  return normalizeProperty(propertySnapshot.id, propertyData, units);
}

export async function addUnitToProperty(propertyId, { label, unitNumber }) {
  const property = await getProperty(propertyId);

  if (!property) {
    throw new Error("We could not find that property.");
  }

  const normalizedUnitNumber = String(unitNumber || "").trim();

  if (!normalizedUnitNumber) {
    throw new Error("Enter a unit number before adding it.");
  }

  const unitId = createUnitDocumentId(normalizedUnitNumber);
  const duplicateUnit = property.units.some(
    (unit) => unit.id.toLowerCase() === unitId.toLowerCase()
  );

  if (duplicateUnit) {
    throw new Error("That unit already exists for this property.");
  }

  await setDoc(
    doc(db, createUnitsCollectionPath(property.id), unitId),
    {
      Available: false,
      label: String(label || "").trim() || unitId,
      legacyId: `Room${normalizedUnitNumber}`,
      storagePath: createUnitStoragePath(property.propertyStoragePath, normalizedUnitNumber),
    },
    { merge: true }
  );

  return getProperty(property.id);
}

export async function removeUnitFromProperty(propertyId, unitId) {
  const property = await getProperty(propertyId);

  if (!property) {
    throw new Error("We could not find that property.");
  }

  const unit = property.units.find(
    (currentUnit) => currentUnit.id.toLowerCase() === String(unitId || "").toLowerCase()
  );

  if (!unit) {
    throw new Error("We could not find that unit.");
  }

  await deleteStorageDirectory(unit.storagePath);
  await deleteDoc(doc(db, createUnitsCollectionPath(property.id), unit.id)).catch(() => {});

  return getProperty(property.id);
}

export async function updateUnitAvailability(propertyId, unitId, available) {
  const property = await getProperty(propertyId);

  if (!property) {
    throw new Error("We could not find that property.");
  }

  const unit = property.units.find(
    (currentUnit) => currentUnit.id.toLowerCase() === String(unitId || "").toLowerCase()
  );

  if (!unit) {
    throw new Error("We could not find that unit.");
  }

  await setDoc(
    doc(db, createUnitsCollectionPath(property.id), unit.id),
    {
      Available: Boolean(available),
      AvailableOn: "",
    },
    { merge: true }
  );

  return getProperty(property.id);
}

export async function updateUnitAvailabilityDate(propertyId, unitId, availableOn) {
  const property = await getProperty(propertyId);

  if (!property) {
    throw new Error("We could not find that property.");
  }

  const unit = property.units.find(
    (currentUnit) => currentUnit.id.toLowerCase() === String(unitId || "").toLowerCase()
  );

  if (!unit) {
    throw new Error("We could not find that unit.");
  }

  const normalizedDate = normalizeAvailabilityDate(availableOn);

  await setDoc(
    doc(db, createUnitsCollectionPath(property.id), unit.id),
    {
      Available: false,
      AvailableOn: normalizedDate,
    },
    { merge: true }
  );

  return getProperty(property.id);
}

export async function updatePropertyDescription(propertyId, description) {
  const property = await getProperty(propertyId);

  if (!property) {
    throw new Error("We could not find that property.");
  }

  await setDoc(
    doc(db, createPropertyDocumentPath(property.id)),
    {
      description: String(description || "").trim(),
    },
    { merge: true }
  );

  return getProperty(property.id);
}

export async function updatePropertyMapLocation(propertyId, mapQuery) {
  const property = await getProperty(propertyId);

  if (!property) {
    throw new Error("We could not find that property.");
  }

  await setDoc(
    doc(db, createPropertyDocumentPath(property.id)),
    {
      mapQuery: String(mapQuery || "").trim(),
    },
    { merge: true }
  );

  return getProperty(property.id);
}

export async function updatePropertyAddress(propertyId, address) {
  const property = await getProperty(propertyId);

  if (!property) {
    throw new Error("We could not find that property.");
  }

  const nextAddress = String(address || "").trim();

  await setDoc(
    doc(db, createPropertyDocumentPath(property.id)),
    {
      address: nextAddress,
      mapQuery: nextAddress,
    },
    { merge: true }
  );

  return getProperty(property.id);
}

export async function deleteProperty(propertyId) {
  const property = await getProperty(propertyId);

  if (!property) {
    throw new Error("We could not find that property.");
  }

  await deleteStorageDirectory(property.propertyStoragePath);

  await Promise.all(
    property.units.map(async (unit) => {
      await deleteDoc(doc(db, createUnitsCollectionPath(property.id), unit.id)).catch(() => {});
    })
  );

  await deleteDoc(doc(db, createPropertyDocumentPath(property.id))).catch(() => {});
}
